/**
 * Emit a diagnostic signal for a state worth OBSERVING but not worth ALERTING
 * on — the non-paging tier below `reportSilentFailure`.
 *
 * Same structured console line (so it still reaches Kibana as an OTel log via
 * the console bridge in `lib/otel/logger.ts`, and the Mixpanel session-replay
 * console panel), but deliberately NO `Sentry.captureMessage` — so it never
 * becomes an issue and never fires a Slack alert.
 *
 * This module imports nothing from Sentry ON PURPOSE. "Cannot raise an issue"
 * is enforced by the module boundary rather than by a convention someone has
 * to remember, so a future edit can't quietly promote this tier back into the
 * issue stream.
 *
 * Use this when a state is self-healing or is the expected consequence of a
 * constraint we don't control, and the value is the TREND (queryable in
 * Kibana / recoverable from a replay), not an alert. Use
 * `reportSilentFailure` when a human should look at it. Current users:
 *
 * - `[Boot] Splash recovered after stuck report` — the paired false-alarm
 *   classifier for `boot-splash-stuck`.
 * - `[Boot] Boot-critical data recovered after grace retries` — grace saved
 *   the boot; the user never saw the error page.
 * - `[Auth] Silent token renewal failed` / `[Auth] Access token expired
 *   without successful silent renewal` — bounded by the SSO Session Idle cap
 *   we cannot lift (see `resolveAuthFailureReport.ts`).
 * - `sentry-replay-load-failed` — the lazy Sentry Replay chunk failed to
 *   load, or `addIntegration` threw after it loaded (`errorName` tells them
 *   apart); the session has no Sentry replay (`lib/lazySentryReplay.ts`).
 * - `direct-bff-mutation-network-error` — a mutation sent straight to the
 *   BFF origin got no response (`FETCH_ERROR`, not caller-aborted), so its
 *   outcome is unknown; tags carry the templatized path only
 *   (`redux/services/baseQuery.ts`).
 *
 * Conventions match `reportSilentFailure`: `[Boot]` / `[Auth]` category
 * prefix on `message`, and PII-safe tags/extra only (derived booleans and
 * fixed-vocabulary codes — never raw URLs, query strings or user input).
 */
export function noteSilentSignal({
  message,
  signal,
  level = "warning",
  tags,
  extra,
}: {
  message: string;
  /**
   * Stable kebab-case key for querying, e.g. "boot-splash-recovered". Kept
   * identical to the retired Sentry fingerprint where one existed, so a saved
   * triage query still finds the same class of event in Kibana.
   */
  signal: string;
  level?: "warning" | "error";
  tags?: Record<string, string>;
  extra?: Record<string, string | number | boolean>;
}): void {
  const consoleFn = level === "error" ? console.error : console.warn;
  consoleFn(
    `[silent-signal] ${message}`,
    JSON.stringify({ signal, tags, extra })
  );
}
